import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Car, Plus, Search, Filter, TrendingUp, AlertTriangle, CheckCircle, Gauge, FileWarning, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useVehicles, useDrivers, VEHICLE_CATEGORIES } from "@/hooks/useVehicles";
import { VehicleCard } from "@/components/fleet/VehicleCard";
import { VehicleDialog } from "@/components/fleet/VehicleDialog";
import { DriverCard } from "@/components/fleet/DriverCard";
import { DriverDialog, type DriverInsert } from "@/components/fleet/DriverDialog";
import { SEO } from "@/components/seo/SEO";
import { supabase } from "@/integrations/supabase/client";
import { B2BModuleGate, ModuleLimitBanner } from "@/components/b2b";
import type { VehicleInsert, Driver } from "@/hooks/useVehicles";

function FleetContent() {
  const navigate = useNavigate();
  const { user, status } = useAuth();
  const { vehicles, isLoading, createVehicle } = useVehicles();
  const { drivers, isLoading: driversLoading, createDriver, updateDriver, deleteDriver } = useDrivers();

  // Fetch vehicle counts per driver
  const { data: vehicleCounts = {} } = useQuery({
    queryKey: ["driver-vehicle-counts", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vehicle_drivers")
        .select("driver_id")
        .is("unassigned_at", null);
      
      if (error) throw error;
      
      const counts: Record<string, number> = {};
      data.forEach(row => {
        counts[row.driver_id] = (counts[row.driver_id] || 0) + 1;
      });
      return counts;
    },
    enabled: !!user?.id,
  });
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [driverDialogOpen, setDriverDialogOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const authLoading = status === "loading";
  const [searchQuery, setSearchQuery] = useState("");
  const [driverSearchQuery, setDriverSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [activeTab, setActiveTab] = useState("vehicles");

  // Calculate stats
  const stats = useMemo(() => {
    const total = vehicles.length;
    const compliant = vehicles.filter(v => v.compliance_score >= 80).length;
    const toCheck = vehicles.filter(v => v.compliance_score >= 50 && v.compliance_score < 80).length;
    const nonCompliant = vehicles.filter(v => v.compliance_score < 50).length;
    const totalMileage = vehicles.reduce((sum, v) => sum + v.current_mileage, 0);
    
    return { total, compliant, toCheck, nonCompliant, totalMileage };
  }, [vehicles]);

  // Filter vehicles
  const filteredVehicles = useMemo(() => {
    return vehicles.filter(vehicle => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        vehicle.license_plate.toLowerCase().includes(searchLower) ||
        vehicle.brand.toLowerCase().includes(searchLower) ||
        vehicle.model.toLowerCase().includes(searchLower);
      
      // Category filter
      const matchesCategory = categoryFilter === "all" || vehicle.category === categoryFilter;
      
      // Status filter
      const matchesStatus = statusFilter === "all" || vehicle.status === statusFilter;
      
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [vehicles, searchQuery, categoryFilter, statusFilter]);

  // Filter drivers
  const filteredDrivers = useMemo(() => {
    if (!driverSearchQuery) return drivers;
    const searchLower = driverSearchQuery.toLowerCase();
    return drivers.filter(driver => {
      const fullName = `${driver.first_name} ${driver.last_name}`.toLowerCase();
      return fullName.includes(searchLower) || driver.email.toLowerCase().includes(searchLower);
    });
  }, [drivers, driverSearchQuery]);

  const handleCreateVehicle = async (data: VehicleInsert) => {
    await createVehicle.mutateAsync(data);
  };

  const handleCreateDriver = async (data: DriverInsert) => {
    await createDriver.mutateAsync(data);
  };

  const handleUpdateDriver = async (data: DriverInsert) => {
    if (!editingDriver) return;
    await updateDriver.mutateAsync({ id: editingDriver.id, ...data });
    setEditingDriver(null);
  };

  const handleEditDriver = (driver: Driver) => {
    setEditingDriver(driver);
    setDriverDialogOpen(true);
  };

  const handleDeleteDriver = async (id: string) => {
    await deleteDriver.mutateAsync(id);
  };

  const handleDriverDialogClose = (open: boolean) => {
    setDriverDialogOpen(open);
    if (!open) setEditingDriver(null);
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    navigate("/auth");
    return null;
  }

  return (
    <>
      <SEO 
        title="Ma Flotte - EasyFinder Fleet"
        description="Gérez la conformité documentaire de votre flotte de véhicules"
      />

      <div className="container max-w-7xl py-6 space-y-6">
        {/* Limit Banner */}
        <ModuleLimitBanner module="fleet" />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Car className="h-7 w-7 text-primary" />
              Ma Flotte
            </h1>
            <p className="text-muted-foreground mt-1">
              Gérez vos véhicules et conducteurs
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/fleet/fines')} className="gap-2">
              <FileWarning className="h-4 w-4" />
              Infractions
            </Button>
            {activeTab === "vehicles" ? (
              <Button onClick={() => setVehicleDialogOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Ajouter un véhicule
              </Button>
            ) : (
              <Button onClick={() => setDriverDialogOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Ajouter un conducteur
              </Button>
            )}
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Car className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Véhicules</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.compliant}</p>
                  <p className="text-xs text-muted-foreground">Conformes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <TrendingUp className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{stats.toCheck}</p>
                  <p className="text-xs text-muted-foreground">À vérifier</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.nonCompliant}</p>
                  <p className="text-xs text-muted-foreground">Non conformes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{drivers.length}</p>
                  <p className="text-xs text-muted-foreground">Conducteurs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="vehicles" className="gap-2">
              <Car className="h-4 w-4" />
              Véhicules ({vehicles.length})
            </TabsTrigger>
            <TabsTrigger value="drivers" className="gap-2">
              <Users className="h-4 w-4" />
              Conducteurs ({drivers.length})
            </TabsTrigger>
          </TabsList>

          {/* Vehicles Tab */}
          <TabsContent value="vehicles" className="mt-4 space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par immatriculation, marque ou modèle..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-44">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {Object.entries(VEHICLE_CATEGORIES).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-36">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="sold">Vendu</SelectItem>
                  <SelectItem value="scrapped">Réformé</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Vehicle grid */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Skeleton key={i} className="h-64" />
                ))}
              </div>
            ) : filteredVehicles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredVehicles.map((vehicle) => (
                  <VehicleCard
                    key={vehicle.id}
                    vehicle={vehicle}
                    onClick={() => navigate(`/fleet/${vehicle.id}`)}
                  />
                ))}
              </div>
            ) : vehicles.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Car className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold mb-1">Aucun véhicule</h3>
                  <p className="text-muted-foreground mb-4">
                    Commencez par ajouter votre premier véhicule à la flotte
                  </p>
                  <Button onClick={() => setVehicleDialogOpen(true)} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Ajouter un véhicule
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Search className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold mb-1">Aucun résultat</h3>
                  <p className="text-muted-foreground">
                    Aucun véhicule ne correspond à votre recherche
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Drivers Tab */}
          <TabsContent value="drivers" className="mt-4 space-y-4">
            {/* Search */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un conducteur..."
                value={driverSearchQuery}
                onChange={(e) => setDriverSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Drivers grid */}
            {driversLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-48" />
                ))}
              </div>
            ) : filteredDrivers.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredDrivers.map((driver) => (
                  <DriverCard
                    key={driver.id}
                    driver={driver}
                    vehiclesCount={vehicleCounts[driver.id] || 0}
                    onEdit={() => handleEditDriver(driver)}
                    onDelete={() => handleDeleteDriver(driver.id)}
                  />
                ))}
              </div>
            ) : drivers.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold mb-1">Aucun conducteur</h3>
                  <p className="text-muted-foreground mb-4">
                    Ajoutez vos conducteurs pour les assigner à des véhicules
                  </p>
                  <Button onClick={() => setDriverDialogOpen(true)} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Ajouter un conducteur
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Search className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold mb-1">Aucun résultat</h3>
                  <p className="text-muted-foreground">
                    Aucun conducteur ne correspond à votre recherche
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        <VehicleDialog
          open={vehicleDialogOpen}
          onOpenChange={setVehicleDialogOpen}
          onSubmit={handleCreateVehicle}
        />
        
        <DriverDialog
          open={driverDialogOpen}
          onOpenChange={handleDriverDialogClose}
          driver={editingDriver}
          onSubmit={editingDriver ? handleUpdateDriver : handleCreateDriver}
        />
      </div>
    </>
  );
}

export default function Fleet() {
  return (
    <B2BModuleGate module="fleet">
      <FleetContent />
    </B2BModuleGate>
  );
}
